import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, FileText, Mail } from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import FiveRangeSection from "@/components/FiveRangeSection";
import ContactForm from "@/components/ContactForm";
import BrochureDownloadButton from "@/components/BrochureDownloadButton";
import CataloguesSection from "@/components/CataloguesSection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const initialCategory = params.get("category") || "all";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(initialCategory);
  const [search, setSearch] = useState("");
  const [showInfoDialog, setShowInfoDialog] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    const q = {};
    if (activeCat !== "all") q.category_id = activeCat;
    if (search) q.search = search;
    api.get("/products", { params: q }).then((r) => setProducts(r.data));
  }, [activeCat, search]);

  useEffect(() => {
    if (activeCat === "all") setParams({});
    else setParams({ category: activeCat });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat]);

  return (
    <>
      <SEO
        title="Veterinary Feed Supplements & Animal Nutrition Products"
        description="Browse Forage's range of veterinary feed supplements, mineral premixes, liquid supplements, nutritional formulations and animal health products — manufactured in India under GMP & WHO-GMP guidelines."
        path="/products"
        rawTitle
      />

      {/* Header */}
      <section className="bg-[#0B2545] text-white py-20 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <span className="overline text-[#2A9D8F]">Our Products</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 leading-tight max-w-3xl">
            Pharmaceutical-grade veterinary formulations.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl">
            Liquid supplements, chelated mineral premixes, feed additives,
            nutritional blends and animal health products — manufactured under
            GMP and WHO-GMP guidelines.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="bg-white border-b border-slate-200 sticky top-[72px] z-30">
        <div className="container-fr px-6 md:px-12 lg:px-20 py-5 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 flex flex-wrap gap-2">
            <button
              data-testid="filter-all"
              onClick={() => setActiveCat("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCat === "all"
                  ? "bg-[#0B2545] text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All Products
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                data-testid={`filter-${c.id}`}
                onClick={() => setActiveCat(c.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCat === c.id
                    ? "bg-[#0B2545] text-white"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="product-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A9D8F]"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr">
          {products.length === 0 ? (
            <div data-testid="no-products" className="text-center py-20 text-slate-500">
              No products match your filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <FiveRangeSection />

      {/* Request Product Information CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#0B2545] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <div className="max-w-3xl">
            <span className="overline text-[#C8A14B]">Bulk Buyers & Distributors</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
              Request product information.
            </h2>
            <p className="text-slate-300 mt-5 max-w-xl leading-relaxed">
              Get technical datasheets, certificate of analysis, pack sizes,
              MOQ and pricing for any product in our catalogue. Our team
              responds within 24 hours.
            </p>
            <div className="flex flex-wrap gap-4 mt-7">
              <button
                type="button"
                onClick={() => setShowInfoDialog(true)}
                data-testid="request-product-info-btn"
                className="bg-[#2A9D8F] hover:bg-[#1F7A6F] text-white px-7 py-3 rounded-md font-medium transition-all hover:-translate-y-0.5 shadow-lg inline-flex items-center gap-2"
              >
                <Mail size={16} /> Request Product Information
              </button>
              <BrochureDownloadButton
                className="bg-[#C8A14B] text-[#0B2545] px-7 py-3 rounded-md font-semibold hover:bg-[#b48d3a] transition-all hover:-translate-y-0.5 shadow-md"
                label="Download Catalogue"
                testId="products-brochure-btn"
              />
            </div>
          </div>
        </div>
      </section>

      <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
        <DialogContent className="max-w-2xl bg-white" data-testid="product-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0B2545]">
              Request Product Information
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Share your requirement and our team will respond within 24 hours with datasheets, COA, MOQ and pricing.
            </DialogDescription>
          </DialogHeader>
          <ContactForm
            defaultType="product"
            defaultSubject="Product Information Request"
            defaultMessage={`Please share datasheets, pack sizes, MOQ and pricing for: ${activeCat === "all" ? "Forage catalogue" : categories.find((c) => c.id === activeCat)?.name || activeCat}`}
            compact
            testIdPrefix="product-info"
          />
        </DialogContent>
      </Dialog>

      {/* CATALOGUES */}
      <CataloguesSection
        title="Or grab a complete catalogue."
        subtitle="Full PDF catalogues by species — Cattle, Poultry, Aqua and Pet — with the entire product range, pack sizes and technical details."
        testIdPrefix="products-catalogue"
      />
    </>
  );
}
